import React from 'react';
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import SettingsIcon from '@atlaskit/icon/core/migration/settings--editor-settings';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { getHrefWithBackLink } from '@atlassian/jira-project-settings-apps-common/src/backlink.tsx';
import type { view_issueViewFoundation_ConfigurationButton$key } from '@atlassian/jira-relay/src/__generated__/view_issueViewFoundation_ConfigurationButton.graphql';
import { useRouterActions } from '@atlassian/react-resource-router';
import issueActionViewMessages from '../issue-actions/messages.tsx';
import Wrapper from './styled.tsx';

type Props = {
	issueViewRelayFragment: view_issueViewFoundation_ConfigurationButton$key | null;
};

export const ConfigurationButton = ({ issueViewRelayFragment }: Props) => {
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	// We're in a non-tangerine folder structure, I can confirm this is a UI component and it isn't sharing state outside itself
	// eslint-disable-next-line @atlassian/relay/query-restriction
	const data = useFragment<view_issueViewFoundation_ConfigurationButton$key>(
		graphql`
			fragment view_issueViewFoundation_ConfigurationButton on JiraIssue {
				key
				configurationUrl
			}
		`,
		issueViewRelayFragment,
	);

	const issueKey = data?.key;
	const href = data?.configurationUrl;
	if (!href) {
		return null;
	}

	const buttonText = formatMessage(issueActionViewMessages.configure);
	const hrefWithBackLink = getHrefWithBackLink(
		href,
		formatMessage(issueActionViewMessages.swiftBacklink, { issueKey }),
	);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onClick = (event: any, analyticsEvent: any) => {
		event.preventDefault(); // This is required as CMP's event propogation triggers full page reload
		fireUIAnalytics(analyticsEvent, 'configureButton');
		push(hrefWithBackLink);
	};

	return (
		<ErrorBoundary prefixOverride="issue" id="Safe(ConfigurationButton)">
			<Wrapper>
				<Button
					/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
					testId="issue.views.issue-base.foundation.configuration-button.configuration-button"
					appearance="subtle-link"
					iconBefore={<SettingsIcon color="currentColor" label="" />}
					href={hrefWithBackLink}
					onClick={onClick}
					interactionName="auto-generated.issue.views.issue-base.foundation.configuration-button.configuration-button.button"
				>
					{buttonText}
				</Button>
			</Wrapper>
		</ErrorBoundary>
	);
};
